import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMuayene, getTemelMedikal, getPersantil, getDokumanlar, getDokumanBlob, getAsilar } from '../services/api';

const trTarih = (s) => s ? new Date(s).toLocaleDateString('tr-TR') : '—';

const trTarihUzun = (s) => {
  if (!s || !s.trim()) return '';
  // DD/MM/YYYY (masaüstü uygulaması bu formatta saklar)
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmy) {
    const d = new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
    if (!isNaN(d)) return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  // YYYY-MM-DD (ISO — backend hesaplamaları)
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(+iso[1], +iso[2] - 1, +iso[3]);
    if (!isNaN(d)) return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  return s;
};

// Düz metni satır sonlarını koruyarak göster
function Metin({ baslik, metin }) {
  if (!metin || !metin.trim()) return null;
  return (
    <div className="kart kart-pad" style={{ marginBottom: 14 }}>
      <div className="bolum-baslik">{baslik}</div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.6, color: 'var(--metin)' }}>{metin}</div>
    </div>
  );
}

// Etiket + değer listesi (Anamnez-2, Sabit Medikal Bilgiler gibi tasarım-tabanlı bölümler)
function EtiketliListe({ baslik, liste }) {
  if (!liste || liste.length === 0) return null;
  return (
    <div className="kart kart-pad" style={{ marginBottom: 14 }}>
      <div className="bolum-baslik">{baslik}</div>
      <div style={{ marginTop: 8 }}>
        {liste.map((x, i) => x.tip === 'baslik'
          ? <div key={i} style={{ fontWeight: 700, color: 'var(--mor)', marginTop: i === 0 ? 0 : 12, marginBottom: 4 }}>{x.etiket}</div>
          : <div key={i} style={{ fontSize: 14, padding: '3px 0' }}><strong>{x.etiket}:</strong> {x.deger}</div>
        )}
      </div>
    </div>
  );
}

function AsilarKart({ kayitNo }) {
  const [data, setData] = useState(null);
  const [yuklendi, setYuklendi] = useState(false);

  useEffect(() => {
    getAsilar(kayitNo)
      .then(r => { setData(r.data); setYuklendi(true); })
      .catch(() => setYuklendi(true));
  }, [kayitNo]);

  if (!yuklendi) return null;
  if (!data || (!data.asilar?.length && !data.meningokok)) return null;

  const { periyotAdlari, asilar, meningokok } = data;

  return (
    <div className="kart kart-pad" style={{ marginBottom: 14 }}>
      <div className="bolum-baslik">💉 Aşılar</div>

      {/* Ana aşı tablosu */}
      {asilar?.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 650, border: '1px solid #ccc' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '5px 8px', background: 'var(--mor)', color: '#fff', whiteSpace: 'nowrap', minWidth: 130, border: '1px solid #9c77cc' }}>Aşı</th>
                {periyotAdlari.map((p, i) => (
                  <th key={i} style={{ padding: '5px 6px', background: 'var(--mor)', color: '#fff', textAlign: 'center', whiteSpace: 'nowrap', minWidth: 70, border: '1px solid #9c77cc' }}>{p}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {asilar.map((a, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? '#fafafa' : '#fff' }}>
                  <td style={{ padding: '4px 8px', fontWeight: 600, whiteSpace: 'nowrap', border: '1px solid #ddd' }}>{a.adi}</td>
                  {a.slotlar.map((s, ci) => {
                    const tdBase = { border: '1px solid #ddd', textAlign: 'center', fontSize: 11, whiteSpace: 'nowrap', padding: '3px 4px' };
                    if (s === null) return <td key={ci} style={{ ...tdBase, color: '#bbb', background: '#f0f0f0' }}>—</td>;
                    if (s.durum === 'bos') return <td key={ci} style={tdBase}></td>;
                    if (s.durum === 'yapildi') return (
                      <td key={ci} style={{ ...tdBase, background: '#4caf50', color: '#fff', fontWeight: 700 }}>
                        {s.tarih ? trTarihUzun(s.tarih) : '✓'}
                      </td>
                    );
                    return (
                      <td key={ci} style={{ ...tdBase, background: '#ef5350', color: '#fff', fontWeight: 600 }}>
                        {s.beklenen ? trTarihUzun(s.beklenen) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Özel Aşılar — 5 statik satır × 3 doz */}
      {meningokok?.satirlar && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--sinir)' }}>
          <div className="bolum-baslik">💉 Özel Aşılar</div>
          <div style={{ overflowX: 'auto', marginTop: 10 }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12, border: '1px solid #ccc' }}>
              <thead>
                <tr>
                  <th style={{ padding: '5px 10px', background: 'var(--mor)', color: '#fff', textAlign: 'left', border: '1px solid #9c77cc', minWidth: 160 }}>Aşı</th>
                  <th style={{ padding: '5px 10px', background: 'var(--mor)', color: '#fff', textAlign: 'left', border: '1px solid #9c77cc', minWidth: 180 }}>Açıklama</th>
                  {[1, 2, 3].map(n => (
                    <th key={n} style={{ padding: '5px 14px', background: 'var(--mor)', color: '#fff', textAlign: 'center', border: '1px solid #9c77cc', minWidth: 110 }}>{n}. Doz</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meningokok.satirlar.map((s, ri) => (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? '#fafafa' : '#fff' }}>
                    <td style={{ padding: '5px 10px', border: '1px solid #ddd', fontWeight: 600, whiteSpace: 'nowrap' }}>{s.ad}</td>
                    <td style={{ padding: '5px 10px', border: '1px solid #ddd', fontSize: 11, color: 'var(--metin-soluk)' }}>{s.aciklama || ''}</td>
                    {s.dozlar.map((d, di) => (
                      <td key={di} style={{
                        border: '1px solid #ddd', textAlign: 'center', padding: '4px 6px', fontSize: 11, fontWeight: 700,
                        background: d.yapildi ? '#4caf50' : '#ef5350',
                        color: '#fff'
                      }}>
                        {d.yapildi ? (d.tarih ? trTarihUzun(d.tarih) : '✓') : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function DokumanlarKart({ kayitNo }) {
  const [liste, setListe] = useState(null);
  const [aciliyor, setAciliyor] = useState(null);

  useEffect(() => {
    getDokumanlar(kayitNo).then(r => setListe(r.data)).catch(() => setListe([]));
  }, [kayitNo]);

  if (!liste || liste.length === 0) return null;

  const ac = async (id, aciklama) => {
    setAciliyor(id);
    try {
      const r = await getDokumanBlob(id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert('Döküman açılamadı.');
    } finally {
      setAciliyor(null);
    }
  };

  return (
    <div className="kart kart-pad" style={{ marginBottom: 14 }}>
      <div className="bolum-baslik">📎 Dökümanlar</div>
      <ul style={{ margin: '8px 0 0', paddingLeft: 0, listStyle: 'none' }}>
        {liste.map((d) => (
          <li key={d.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--sinir)' }}>
            <button
              onClick={() => ac(d.id, d.aciklama)}
              disabled={aciliyor === d.id}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mor)', fontSize: 14, textAlign: 'left', padding: 0 }}
            >
              {aciliyor === d.id ? '…' : '📄'} {d.tar && <span style={{ color: 'var(--metin-soluk)', marginRight: 6 }}>{trTarihUzun(d.tar)}</span>}{d.aciklama || '(adsız döküman)'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SabitMedikalKart({ kayitNo }) {
  const [liste, setListe] = useState(null);
  useEffect(() => {
    getTemelMedikal(kayitNo).then(r => setListe(r.data)).catch(() => setListe([]));
  }, [kayitNo]);

  if (!liste || liste.length === 0) return null;

  return (
    <div className="kart kart-pad" style={{ marginBottom: 14 }}>
      <div className="bolum-baslik">🩺 Sabit Medikal Bilgiler</div>
      <div style={{ marginTop: 8 }}>
        {liste.map((x, i) => x.tip === 'baslik'
          ? <div key={i} style={{ fontWeight: 700, color: 'var(--mor)', marginTop: i === 0 ? 0 : 12, marginBottom: 4 }}>{x.etiket}</div>
          : <div key={i} style={{ fontSize: 14, padding: '3px 0' }}><strong>{x.etiket}:</strong> {x.deger}</div>
        )}
      </div>
    </div>
  );
}

const PERSANTIL_RENKLER = {
  '%3':  { bg: '#ef5350', fg: '#fff' },
  '%10': { bg: '#ff9800', fg: '#fff' },
  '%25': { bg: '#ffee58', fg: '#333' },
  '%50': { bg: '#66bb6a', fg: '#fff' },
  '%75': { bg: '#42a5f5', fg: '#fff' },
  '%90': { bg: '#ab47bc', fg: '#fff' },
  '%97': { bg: '#8d6e63', fg: '#fff' },
};

const PERSANTIL_SIRA = ['%3', '%10', '%25', '%50', '%75', '%90', '%97'];

function persantilStil(durum) {
  if (!durum) return {};
  const r = PERSANTIL_RENKLER[durum];
  if (!r) return {};
  return { backgroundColor: r.bg, color: r.fg, fontWeight: 600, borderRadius: 4, padding: '2px 6px', display: 'inline-block' };
}

function PersantilKart({ kayitNo }) {
  const [liste, setListe] = useState(null);
  useEffect(() => {
    getPersantil(kayitNo).then(r => setListe(r.data)).catch(() => setListe([]));
  }, [kayitNo]);

  if (!liste || liste.length === 0) return null;

  return (
    <div className="kart kart-pad" style={{ marginBottom: 14 }}>
      <div className="bolum-baslik">📈 Persantil</div>

      {/* Lejant */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0 12px' }}>
        {PERSANTIL_SIRA.map(p => (
          <span key={p} style={{ ...persantilStil(p), fontSize: 12 }}>{p}</span>
        ))}
      </div>

      <table className="tablo">
        <thead>
          <tr><th>Zaman</th><th>Boy</th><th>Kilo</th><th>BÇ</th><th>VKİ</th></tr>
        </thead>
        <tbody>
          {liste.map((p, i) => (
            <tr key={i}>
              <td>{p.zaman}</td>
              <td><span style={persantilStil(p.boyDurum)}>{p.boy}</span></td>
              <td><span style={persantilStil(p.kiloDurum)}>{p.kilo}</span></td>
              <td><span style={persantilStil(p.bcDurum)}>{p.bc}</span></td>
              <td><span style={persantilStil(p.vkiDurum)}>{p.vki}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MuayeneDetay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [hata, setHata] = useState('');

  useEffect(() => {
    getMuayene(id).then(r => setM(r.data)).catch(() => setHata('Muayene bulunamadı.'));
  }, [id]);

  if (hata) return <div className="kart kart-pad">{hata}</div>;
  if (!m) return <div className="kart kart-pad">Yükleniyor…</div>;

  const bosMu = (m.bloklar || []).length === 0 && (m.anamnez2 || []).length === 0 && !m.genelAnamnez && !m.oneriler && !m.tedaviPlan
    && (m.tanilar || []).length === 0 && (m.ilaclar || []).length === 0;

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span onClick={() => navigate('/muayeneler')}
              style={{ color: 'var(--mor)', cursor: 'pointer', fontSize: 13.5, fontWeight: 600 }}>
          ← Muayene Listesine Geri Dön
        </span>
        <button className="btn-ikincil" onClick={() => navigate(`/hasta/${m.kayitNo}`, { state: { from: 'muayene-detay', fromLabel: 'Muayene Dosyasına Geri Dön' } })}>
          Hasta Kartı
        </button>
        <span onClick={() => navigate(`/hasta/${m.kayitNo}/muayeneler`)}
              style={{ color: 'var(--metin-soluk)', cursor: 'pointer', fontSize: 12.5 }}>
          Bu hastanın muayene geçmişi →
        </span>
      </div>

      {/* Üst bilgi */}
      <div className="kart kart-pad" style={{ marginBottom: 16 }}>
        <h1 className="sayfa-baslik" style={{ marginBottom: 8 }}>{m.adSoy || 'Hasta'} — {trTarih(m.tar)}</h1>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {m.saat && <span className="bilgi-rozet">🕐 {m.saat}</span>}
          {m.doktor && <span className="bilgi-rozet">👨‍⚕️ {m.doktor}</span>}
          {m.muayeneTuru && <span className="bilgi-rozet">Tür: {m.muayeneTuru}</span>}
          {m.kontrol && <span className="rozet rozet-e">Kontrol</span>}
        </div>
      </div>

      {bosMu && <div className="kart kart-pad" style={{ color: 'var(--metin-soluk)' }}>Bu muayenede metin içeriği bulunmuyor.</div>}

      {(m.bloklar || []).map((b, i) => <Metin key={i} baslik={b.baslik} metin={b.metin} />)}
      <EtiketliListe baslik="Anamnez-2" liste={m.anamnez2} />
      <Metin baslik="Genel Anamnez" metin={m.genelAnamnez} />
      <Metin baslik="Öneriler" metin={m.oneriler} />
      <Metin baslik="Tedavi Planı" metin={m.tedaviPlan} />

      {(m.tanilar || []).length > 0 && (
        <div className="kart kart-pad" style={{ marginBottom: 14 }}>
          <div className="bolum-baslik">🔬 Tanılar</div>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 14, lineHeight: 1.8 }}>
            {m.tanilar.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}

      {(m.ilaclar || []).length > 0 && (
        <div className="kart kart-pad" style={{ marginBottom: 14 }}>
          <div className="bolum-baslik">💊 Reçete</div>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 14, lineHeight: 1.8 }}>
            {m.ilaclar.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}

      <DokumanlarKart kayitNo={m.kayitNo} />
      <AsilarKart kayitNo={m.kayitNo} />
      <SabitMedikalKart kayitNo={m.kayitNo} />
      <PersantilKart kayitNo={m.kayitNo} />
    </div>
  );
}
